import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const fmt = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");

const r10Steps = [
  {
    n: 1, label: "You contribute", color: "#1B3A6B",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 000 4h4a2 2 0 010 4H8M12 6v2M12 16v2"/></svg>,
    title: "Your turn around starts here",
    desc: "R10. That is all it takes. You stop waiting and start building. One contribution. One decision. The beginning of something real."
  },
  {
    n: 2, label: "Your project", color: "#1B3A6B",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>,
    title: "It goes to a specific project",
    desc: "Your R10 is directed to one specific community project you chose. Your money has a destination."
  },
  {
    n: 3, label: "Forge Vault", color: "#2E5FA3",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    title: "Forge Vault takes over",
    desc: "The moment your contribution lands, Forge Vault protects it. Our automated governance engine holds it securely. No one at Forge SA can touch it."
  },
  {
    n: 4, label: "Milestone", color: "#2E5FA3",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    title: "The project reaches a milestone",
    desc: "Nothing moves until the project hits a pre-agreed stage. Each milestone unlocks the next step."
  },
  {
    n: 5, label: "Evidence", color: "#3A6BC4",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    title: "Evidence submitted and verified",
    desc: "Photos, invoices and site reports submitted as proof. Every claim verified independently before anything moves."
  },
  {
    n: 6, label: "Approval", color: "#3A6BC4",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
    title: "Forge Vault checks all 8 rules",
    desc: "All 8 rules must pass automatically. No person makes this decision. The rules do."
  },
  {
    n: 7, label: "Payment", color: "#5B9BD5",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
    title: "Payment released directly to supplier",
    desc: "Forge Vault releases payment directly to the verified supplier or worker. Never through Forge SA."
  },
  {
    n: 8, label: "Delivery", color: "#5B9BD5",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 6v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
    title: "Supplier delivers goods and services",
    desc: "Materials arrive on site. Workers are paid. Progress is photographed and uploaded publicly."
  },
  {
    n: 9, label: "Live Ledger", color: "#7EB3E0",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>,
    title: "Every payment hits the Live Ledger",
    desc: "The moment payment is made it appears on the public Live Ledger. Visible to every contributor. Forever."
  },
  {
    n: 10, label: "Cycle repeats", color: "#C9A84C",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M12 2a9 9 0 019 9c0 4-2.5 7.5-6 9M12 2a9 9 0 00-9 9c0 4 2.5 7.5 6 9M12 2v18"/></svg>,
    title: "Project continues. Impact grows. Cycle repeats.",
    desc: "The factory opens. Jobs are created. A percentage of profits seeds the next project. Your R10 never stops building."
  },
];

const vaultRules = [
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>, text: "Funding goal reached" },
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/></svg>, text: "Supplier verified" },
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, text: "Invoice matches budget" },
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>, text: "Evidence uploaded" },
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>, text: "Community voted 70%+" },
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>, text: "Stage order correct" },
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>, text: "No duplicate payments" },
  { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B8D4E8" strokeWidth="2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>, text: "Banking details verified" },
];

function FeaturedProject() {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchTopProject = async () => {
      const { data } = await supabase
        .from("projects")
        .select("*")
        .gt("raised", 0)
        .order("raised", { ascending: false })
        .limit(1);
      if (data && data.length > 0) setProject(data[0]);
      setLoaded(true);
    };
    fetchTopProject();
  }, []);

  // Nothing to show yet, so show nothing. No placeholder, no fake data.
  if (!loaded || !project) return null;

  const pct = project.goal > 0 ? Math.min(100, Math.round((project.raised / project.goal) * 100)) : 0;

  return (
    <div style={{ background: "#F5F0E8", padding: "48px 24px", borderBottom: "1px solid #EDE8DF" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ display: "inline-block", background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#8a6a1a", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>
            Leading The Way
          </div>
          <h2 style={{ fontSize: "clamp(20px, 4vw, 26px)", fontWeight: 900, letterSpacing: "-0.02em", margin: 0 }}>
            South Africa's <span style={{ color: "#1B3A6B" }}>Most-Backed Project</span>
          </h2>
        </div>

        <div
          onClick={() => navigate("/projects")}
          style={{ background: "#fff", border: "1px solid #EDE8DF", borderRadius: 20, overflow: "hidden", cursor: "pointer" }}
        >
          <div style={{ height: 70, background: "#5B9BD5", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px" }}>
            <span style={{ background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "4px 12px", borderRadius: 99 }}>
              {project.category}
            </span>
            <span style={{ background: "#2E7D32", color: "#fff", fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 99 }}>
              Live
            </span>
          </div>
          <div style={{ padding: 26 }}>
            <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 6 }}>{project.title}</div>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>{project.description}</div>

            <div style={{ background: "#EDE8DF", borderRadius: 99, height: 8, overflow: "hidden", marginBottom: 10 }}>
              <div style={{ background: "#1B3A6B", height: 8, borderRadius: 99, width: `${pct}%` }} />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 22 }}>
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Raised</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1B3A6B" }}>{fmt(project.raised)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Goal</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1B3A6B" }}>{fmt(project.goal)}</div>
              </div>
            </div>

            <button
              onClick={(e) => { e.stopPropagation(); navigate("/projects"); }}
              style={{ display: "block", width: "100%", textAlign: "center", background: "#C9A84C", color: "#fff", fontWeight: 700, fontSize: 14, padding: 13, borderRadius: 99, border: "none", cursor: "pointer", fontFamily: "var(--font-body)" }}
            >
              Contribute Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LifeOfR10() {
  const [active, setActive] = useState(null);
  const cx = 160, cy = 160, r = 110;
  const getPos = (i) => {
    const angle = (270 + i * 36) * (Math.PI / 180);
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  };
  const getLabelPos = (i) => {
    const angle = (270 + i * 36) * (Math.PI / 180);
    const lr = 148;
    return { x: cx + lr * Math.cos(angle), y: cy + lr * Math.sin(angle) };
  };

  return (
    <div style={{ padding: "56px 24px", background: "#fff", borderBottom: "1px solid #EDE8DF" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#1B3A6B", letterSpacing: "0.1em", textTransform: "uppercase", textAlign: "center", marginBottom: 10 }}>How your money works</div>
      <div style={{ fontSize: "clamp(20px, 4vw, 28px)", fontWeight: 900, color: "#1A1A2E", textAlign: "center", letterSpacing: "-0.02em", marginBottom: 8 }}>
        The Life of <span style={{ color: "#1B3A6B" }}>R10</span>
      </div>
      <div style={{ fontSize: 13, color: "#888", textAlign: "center", maxWidth: 440, margin: "0 auto 32px", lineHeight: 1.7 }}>
        Tap each step to follow your contribution from first rand to lasting community impact.
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
        <svg width="100%" viewBox="0 0 320 320" style={{ maxWidth: 340 }} xmlns="http://www.w3.org/2000/svg">
          <circle cx="160" cy="160" r="110" fill="none" stroke="#EDE8DF" strokeWidth="1.5"/>
          {r10Steps.map((s, i) => {
            const pos = getPos(i);
            const isActive = active === s.n;
            const isGold = i === 9;
            const lx = pos.x + (pos.x > cx ? 28 : pos.x < cx ? -28 : 0);
            const ly = pos.y + (pos.y > cy ? 28 : pos.y < cy ? -28 : 0);
            return (
              <g key={s.n} onClick={() => setActive(s.n)} style={{ cursor: "pointer" }}>
                {isActive && <circle cx={pos.x} cy={pos.y} r="20" fill="none" stroke={isGold ? "#BA7517" : "#185FA5"} strokeWidth="2.5"/>}
                <circle cx={pos.x} cy={pos.y} r="15" fill={s.color}/>
                <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="system-ui">{s.n}</text>
                <text x={lx} y={ly - 5} textAnchor="middle" fontSize="8.5" fontWeight="600" fill={s.color} fontFamily="system-ui">{s.label}</text>
                <text x={lx} y={ly + 6} textAnchor="middle" fontSize="7.5" fill="#888" fontFamily="system-ui">Step {s.n}</text>
              </g>
            );
          })}
          <text x="160" y="142" textAnchor="middle" fontSize="8.5" fill="#888" fontFamily="system-ui" letterSpacing="1">THE LIFE OF</text>
          <text x="160" y="168" textAnchor="middle" fontSize="30" fontWeight="900" fill="#1B3A6B" fontFamily="system-ui">R10</text>
          <text x="160" y="184" textAnchor="middle" fontSize="8.5" fill="#888" fontFamily="system-ui">Your contribution.</text>
          <text x="160" y="196" textAnchor="middle" fontSize="8.5" fill="#888" fontFamily="system-ui">Maximum impact.</text>
          <text x="160" y="212" textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#1B3A6B" fontFamily="system-ui">FORGE SA</text>
        </svg>

        {active ? (
          <div style={{ background: "#F5F0E8", border: "1px solid #EDE8DF", borderRadius: 14, padding: "16px 18px", maxWidth: 340, width: "100%" }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>Step {active} of 10</div>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: r10Steps[active - 1].color, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
              {r10Steps[active - 1].icon}
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: active === 10 ? "#C9A84C" : "#1A1A2E", marginBottom: 6, lineHeight: 1.4 }}>{r10Steps[active - 1].title}</div>
            <div style={{ fontSize: 12, color: "#4A4A6A", lineHeight: 1.6 }}>{r10Steps[active - 1].desc}</div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#888", textAlign: "center" }}>Tap any number around the circle</div>
        )}
      </div>
    </div>
  );
}

export default function ForgeSAHome() {
  const navigate = useNavigate();

  const handleNotify = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    navigate(session ? "/dashboard" : "/register");
  };

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100vh", color: "#1A1A2E", fontFamily: "var(--font-body)" }}>

      <style>{`
        .fh-hero { padding: 56px 48px 48px; text-align: center; }
        .fh-hero-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 40px; }
        .fh-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; max-width: 600px; margin: 0 auto; padding: 0 48px 48px; }
        .fh-section { padding: 48px; background: #F5F0E8; }
        .fh-steps-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px; max-width: 860px; margin: 0 auto; }
        .fh-vault-section { padding: 56px 48px; background: #1B3A6B; }
        .fh-vault-rules { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; max-width: 560px; margin: 0 auto; }
        .fh-cta { padding: 64px 48px; background: #0D1B35; text-align: center; }
        @media (max-width: 768px) {
          .fh-hero { padding: 36px 20px 32px; }
          .fh-hero-btns { flex-direction: column; align-items: center; }
          .fh-hero-btns button { width: 100%; max-width: 320px; }
          .fh-stats { grid-template-columns: repeat(3, 1fr); gap: 8px; padding: 0 20px 32px; }
          .fh-section { padding: 32px 20px; }
          .fh-steps-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
          .fh-vault-section { padding: 40px 20px; }
          .fh-vault-rules { grid-template-columns: 1fr 1fr; }
          .fh-cta { padding: 48px 20px; }
          .fh-cta button { width: 100%; }
        }
      `}</style>

      <Navbar />

      {/* HERO */}
      <div style={{ background: "#fff", borderBottom: "1px solid #EDE8DF" }}>
        <div className="fh-hero">
          <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
            Built for South Africa
          </div>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 56px)", fontWeight: 900, lineHeight: 1.05, margin: "0 0 16px", letterSpacing: "-0.02em", color: "#1A1A2E" }}>
            Don't wait for change.<br />
            <span style={{ color: "#C9A84C" }}>Forge it.</span>
          </h1>
          <p style={{ fontSize: 15, color: "#888", lineHeight: 1.8, maxWidth: 480, margin: "0 auto 28px" }}>
            A platform where South Africans fund, build and benefit from community projects together. Transparent. Community driven. Yours.
          </p>
          <div className="fh-hero-btns">
            <button onClick={() => navigate("/projects")} style={{ background: "#1B3A6B", border: "none", borderRadius: 99, padding: "13px 28px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}>
              Explore projects
            </button>
            <button onClick={() => navigate("/howitworks")} style={{ background: "transparent", border: "1.5px solid #EDE8DF", borderRadius: 99, padding: "13px 28px", color: "#4A4A6A", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}>
              How it works
            </button>
          </div>
          <div style={{ marginBottom: 8 }}>
            <button onClick={() => navigate("/impact")} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", border: "none", color: "#1B3A6B", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1B3A6B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              See what your contribution could build
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1B3A6B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
        </div>

        {/* STATS */}
        <div className="fh-stats">
          {[
            { n: "R10", l: "Minimum contribution" },
            { n: "9", l: "Project categories" },
            { n: "0%", l: "Fees to contributors" },
          ].map((s) => (
            <div key={s.l} style={{ background: "#F5F0E8", border: "1px solid #EDE8DF", borderRadius: 14, padding: "14px", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#1B3A6B" }}>{s.n}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURED PROJECT — only appears once a real project has real contributions */}
      <FeaturedProject />

      {/* LIFE OF R10 */}
      <LifeOfR10 />

      {/* FORGE VAULT */}
      <div className="fh-vault-section">
        <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(184,212,232,0.6)", letterSpacing: "0.1em", textTransform: "uppercase", textAlign: "center", marginBottom: 10 }}>
          Governance Engine
        </div>
        <div style={{ fontSize: "clamp(20px, 4vw, 28px)", fontWeight: 900, color: "#fff", textAlign: "center", letterSpacing: "-0.02em", marginBottom: 8 }}>
          Forge <span style={{ color: "#C9A84C" }}>Vault</span>
        </div>
        <div style={{ fontSize: 13, color: "rgba(184,212,232,0.7)", textAlign: "center", maxWidth: 440, margin: "0 auto 28px", lineHeight: 1.7 }}>
          Forge Vault is not a bank. It is the automated engine that enforces every rule before a single rand moves. Nobody controls the money. The rules do.
        </div>
        <div className="fh-vault-rules">
          {vaultRules.map((r, i) => (
            <div key={i} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(184,212,232,0.12)", borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 28, height: 28, background: "rgba(91,155,213,0.15)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {r.icon}
              </div>
              <div style={{ fontSize: 11, color: "rgba(184,212,232,0.8)", fontWeight: 600 }}>{r.text}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <button onClick={() => navigate("/forge-vault")} style={{ background: "rgba(255,255,255,0.1)", border: "1.5px solid rgba(184,212,232,0.3)", borderRadius: 99, padding: "10px 24px", color: "rgba(184,212,232,0.9)", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}>
            See Forge Vault in action
          </button>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="fh-section">
        <div style={{ fontSize: 22, fontWeight: 800, color: "#1A1A2E", textAlign: "center", marginBottom: 6, letterSpacing: "-0.01em" }}>
          How Forge SA works
        </div>
        <div style={{ fontSize: 13, color: "#888", textAlign: "center", marginBottom: 28 }}>
          Transparent from rand one to ribbon cutting
        </div>
        <div className="fh-steps-grid">
          {[
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1B3A6B" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>, bg: "rgba(27,58,107,0.08)", title: "Choose a project", desc: "Browse verified community projects across 9 categories.", gold: false },
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2E5FA3" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 000 4h4a2 2 0 010 4H8M12 6v2M12 16v2"/></svg>, bg: "rgba(46,95,163,0.08)", title: "Contribute", desc: "From R10. Your funds go to Forge Vault. Not to us.", gold: false },
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5B9BD5" strokeWidth="2.2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>, bg: "rgba(91,155,213,0.1)", title: "Track progress", desc: "Every milestone verified with proof before funds release.", gold: false },
            { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>, bg: "rgba(201,168,76,0.1)", title: "See the impact", desc: "Jobs created. Communities built. Cycle repeats.", gold: true },
          ].map((s, i) => (
            <div key={i} style={{ background: "#fff", border: `1px solid ${s.gold ? "#C9A84C" : "#EDE8DF"}`, borderRadius: 16, padding: 20, textAlign: "center" }}>
              <div style={{ width: 44, height: 44, background: s.bg, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                {s.icon}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 11, color: "#888", lineHeight: 1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="fh-cta">
        <h2 style={{ fontSize: "clamp(24px, 4vw, 40px)", fontWeight: 900, color: "#fff", margin: "0 0 14px", letterSpacing: "-0.02em" }}>
          Ready to <span style={{ color: "#C9A84C" }}>Forge</span> South Africa?
        </h2>
        <p style={{ color: "rgba(184,212,232,0.8)", fontSize: 15, marginBottom: 28, maxWidth: 480, margin: "0 auto 28px" }}>
          Join South Africans who stopped waiting and started building. From just R10, your contribution creates lasting change.
        </p>
        <button onClick={() => navigate("/register")} style={{ background: "#C9A84C", border: "none", borderRadius: 99, padding: "14px 36px", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "var(--font-body)" }}>
          Join the Movement
        </button>
        <div style={{ marginTop: 32, fontSize: 12, color: "rgba(184,212,232,0.35)" }}>
          Forge SA. Built by us. For us.
        </div>
      </div>

    </div>
  );
}